import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import Arrow from '../../icons/Arrow'

export interface SidebarItemProps {
  label: string
  href: string
  subItems?: SidebarItemProps[]
  itemColor?: string
  icon?: ReactNode
  isOpen?: boolean
  isActive?: boolean
}

/**
 * @component
 * @param {Object} props
 * @param {string} props.label - The label of the sidebar item.
 * @param {string} props.href - The href link for the sidebar item.
 * @param {SidebarItemProps[]} [props.subItems] - Optional sub-items for the sidebar item.
 * @param {string} [props.itemColor] - Optional color class for the sidebar item. Default is "text-on-primary".
 * @param {ReactNode} [props.icon] - Optional icon component for the sidebar item.
 * @returns {React.ReactElement} The rendered SidebarItem component.
 */

const SidebarItem = ({
  label,
  href,
  subItems,
  itemColor = 'text-on-primary',
  icon,
  isActive: isActiveProp,
}: SidebarItemProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [internalActive, setInternalActive] = useState(false)
  const isControlled = isActiveProp !== undefined
  const isActive = isControlled ? isActiveProp : internalActive

  const hasSubItems = subItems && subItems.length > 0

  useEffect(() => {
    // When a parent (e.g. Sidebar) drives isActive via scrollspy across all
    // items, skip this item's own single-section scroll listener.
    if (isControlled) return
    if (!href.startsWith('#')) return
    const sectionId = href.slice(1)
    const section = document.getElementById(sectionId)
    if (!section) return

    const handleScroll = () => {
      const rect = section.getBoundingClientRect()
      // Consider active if top is within 0-80px from top of viewport
      setInternalActive(rect.top >= 0 && rect.top < 80)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Initial check
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [href, isControlled])

  const handleExpandToggle = () => {
    if (hasSubItems) {
      setIsExpanded(!isExpanded)
    }
  }

  return (
    <div className={isActive ? 'w-full border-r-2 border-blue-600' : 'w-full'}>
      {href.startsWith('#') ? (
        <a
          href={href}
          className={`${itemColor} flex w-full items-center justify-between py-4 hover:text-primary-variant`}
          onClick={handleExpandToggle}
        >
          <div className="">
            {icon && <span className="mr-4">{icon}</span>}
            <span className={isActive ? 'font-bold' : ''}>{label}</span>
          </div>
          {hasSubItems && (
            <Arrow className={`transition-transform ${isExpanded ? 'rotate-90' : '-rotate-90'}`} />
          )}
        </a>
      ) : (
        <Link
          to={href}
          className={`${itemColor} flex w-full items-center justify-between py-4 hover:text-primary-variant`}
          onClick={handleExpandToggle}
        >
          <div className="">
            {icon && <span className="mr-4">{icon}</span>}
            <span className={isActive ? 'font-bold' : ''}>{label}</span>
          </div>
          {hasSubItems && (
            <Arrow className={`transition-transform ${isExpanded ? 'rotate-90' : '-rotate-90'}`} />
          )}
        </Link>
      )}
      {isExpanded && hasSubItems && (
        <div className="pl-10">
          {subItems.map((subItem, index) => (
            <SidebarItem key={index} {...subItem} itemColor={itemColor} />
          ))}
        </div>
      )}
    </div>
  )
}

export default SidebarItem
